import {
  UC_TIERS,
  POSTED_PRICE_SET,
  POSTED_STANDALONE_TOOL_CALL_UC,
  postedInteractionPrice,
} from '@syncanix/shared';
import { Gauge, PauseCircle, Wallet } from 'lucide-react';

import { CtaArrow, ctaClasses } from '@/components/bedrock';
import { useT } from '@/core/I18nProvider';
// Direct module paths, not the feature barrel: the barrel also exports the
// `UcCalculator` component, and the worked example must stay a pure data import.
import { recommendTier } from '@/features/uc-calculator/uc-estimate';
import {
  EXAMPLE_FULL_USAGE_CREDITS_PER_MONTH,
  EXAMPLE_INTERACTIONS_PER_MONTH,
  EXAMPLE_SIMPLE_USAGE_CREDITS_PER_MONTH,
} from '@/features/uc-calculator/usage-example';
import { formatLocaleNumber, formatLocaleNumberList } from '@/i18n/format';

import type { LucideIcon } from 'lucide-react';
import type { ReactElement } from 'react';

/**
 * UcExplainer — the usage-credit value band shown above the tier cards on
 * `/pricing`.
 *
 * It publishes the **posted price menu** (ADR-0442): an interaction costs one of
 * exactly four prices, decided by what the interaction DID, never by what it
 * consumed.
 *
 *     3 UC   the assistant answered a question
 *     6 UC   it answered and took an action
 *     9 UC   it answered and looked something up
 *    12 UC   it did both
 *
 * That closed set is the whole product promise, so the section's job is to let a
 * buyer read the entire menu before they buy. It replaces the per-event weight
 * table (message 2 / tool 2 / endpoint 1 / insight 5), the token anchor rule
 * ("1 UC per $0.0016 of model cost") and the light/typical/heavy spread — all
 * three described a MEASURED price that no longer exists, and the anchor rule in
 * particular is the exact thing posted pricing was adopted to abolish: a charge
 * a customer could not predict before they made it.
 *
 * The PRICES are derived from `postedInteractionPrice`, not typed — the menu on
 * the page and the ladder the meter charges are one function, so they cannot
 * drift; only the labels and prose resolve through `t()`.
 *
 * Brand-voice contract (mirrors `Hero`): lowercase CTA labels, zero exclamation
 * marks, none of the `HERO_FORBIDDEN_WORDS`. Logical Tailwind directionals only
 * (RTL-correct).
 */

/**
 * One colour per rung, taken from the four stops of the brand gradient
 * (`--bd-gradient` in `bedrock.css`) in the order they appear there. Written as
 * token references rather than hexes so a brand change moves them with
 * everything else.
 */
const RUNG_COLORS = [
  'var(--bd-cyan)',
  'var(--bd-blue)',
  'var(--bd-purple)',
  'var(--bd-violet)',
] as const;

/** The four rungs, ascending — computed from the shared price function. */
const POSTED_MENU: readonly { id: string; labelKey: string; uc: number }[] = [
  {
    id: 'answer',
    labelKey: 'pricing.uc.menu.answer',
    uc: postedInteractionPrice({ acted: false, lookedUp: false }),
  },
  {
    id: 'acted',
    labelKey: 'pricing.uc.menu.acted',
    uc: postedInteractionPrice({ acted: true, lookedUp: false }),
  },
  {
    id: 'lookedUp',
    labelKey: 'pricing.uc.menu.lookedUp',
    uc: postedInteractionPrice({ acted: false, lookedUp: true }),
  },
  {
    id: 'both',
    labelKey: 'pricing.uc.menu.both',
    uc: postedInteractionPrice({ acted: true, lookedUp: true }),
  },
];

const PROMISE: readonly { id: string; Icon: LucideIcon; key: string }[] = [
  { id: 'gauge', Icon: Gauge, key: 'pricing.uc.promise.gauge' },
  { id: 'hardStop', Icon: PauseCircle, key: 'pricing.uc.promise.hardStop' },
  { id: 'overage', Icon: Wallet, key: 'pricing.uc.promise.overage' },
];

/**
 * The plan the worked month lands on — DERIVED from the example's own ceiling,
 * so the tier named in the copy can never drift from the tier that actually
 * covers it. Module-level because every input is a compile-time constant.
 */
const EXAMPLE_TIER = UC_TIERS[recommendTier(EXAMPLE_FULL_USAGE_CREDITS_PER_MONTH)];

export function UcExplainer(): ReactElement {
  const { t, locale } = useT();
  const fmt = (n: number): string => formatLocaleNumber(n, locale);
  return (
    <section
      id="how-uc-works"
      aria-labelledby="uc-explainer-heading"
      aria-label={t('pricing.uc.regionLabel')}
      className="border-b border-[color:var(--bd-line-3)] bg-[color:var(--bd-sunk)] px-6 py-16 sm:py-20"
    >
      <div className="mx-auto max-w-5xl">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-medium uppercase tracking-wide text-[color:var(--bd-muted)] rtl:normal-case rtl:tracking-normal">
            {t('pricing.uc.eyebrow')}
          </p>
          <h2
            id="uc-explainer-heading"
            className="mt-3 text-balance text-3xl font-semibold tracking-tight text-[color:var(--bd-ink)] sm:text-4xl"
          >
            {t('pricing.uc.heading')}
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-pretty text-base text-[color:var(--bd-muted)] sm:text-lg">
            {t('pricing.uc.lead')}
          </p>
        </div>

        {/* The whole menu — four posted prices, nothing else exists. */}
        <ul
          aria-label={t('pricing.uc.menu.label')}
          className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4"
        >
          {POSTED_MENU.map(({ id, labelKey, uc }, rung) => (
            <li
              key={id}
              data-uc-price={id}
              className="flex flex-col items-center gap-1 rounded-[14px] border border-[color:var(--bd-line)] bg-gradient-to-b from-[color:var(--bd-raise)] to-[color:var(--bd-page)] p-5 pt-6 text-center"
            >
              {/* The rung indicator. The four prices are exactly 1×, 2×, 3× and
                  4× the base, so N filled segments is a truthful picture of the
                  ladder rather than a decoration — it says "the price is set by
                  what the interaction DID" in one glance, which is the section's
                  whole argument and otherwise takes four labels to read.

                  Each rung takes its own stop from the brand ramp rather than
                  the whole gradient: a 20px segment compresses the full
                  cyan→violet sweep into a single muddy blue, so four such
                  segments look identical and the ladder stops reading. Per-stop
                  colours also survive RTL without arithmetic, where a
                  `background-position` slice across a reversed flex row would
                  not. Decorative only — the numeral and the label carry the
                  meaning, so `aria-hidden` is correct and no contrast floor
                  applies. */}
              <span aria-hidden="true" className="mb-2.5 flex items-center justify-center gap-1">
                {POSTED_MENU.map((_, i) => (
                  <span
                    key={i}
                    className="h-[5px] w-5 rounded-full"
                    style={{ background: i <= rung ? RUNG_COLORS[i] : 'var(--bd-line-3)' }}
                  />
                ))}
              </span>
              <span className="text-[2rem] font-semibold leading-none tracking-[-0.04em] tabular-nums text-[color:var(--bd-ink)]">
                {uc}{' '}
                <span className="text-base font-normal tracking-normal text-[color:var(--bd-muted)]">
                  UC
                </span>
              </span>
              <span className="mt-1.5 text-pretty text-[0.86rem] leading-[1.5] text-[color:var(--bd-muted)]">
                {t(labelKey)}
              </span>
            </li>
          ))}
        </ul>

        {/* The closed-set promise, and the one shape that is not a chat turn: a
            tool call arriving straight from Claude Desktop / Cursor / a ChatGPT
            connector, with no question and no generated answer. */}
        <p
          data-uc-closed-set
          className="mx-auto mt-8 max-w-3xl rounded-lg border border-[color:var(--bd-line)] bg-[color:var(--bd-page)] p-5 text-pretty text-center text-sm text-[color:var(--bd-muted)] sm:text-base"
        >
          <span className="font-medium text-[color:var(--bd-ink)]">
            {t('pricing.uc.closedSet.label', {
              prices: formatLocaleNumberList(POSTED_PRICE_SET, locale),
            })}
          </span>{' '}
          {t('pricing.uc.closedSet', { standalone: fmt(POSTED_STANDALONE_TOOL_CALL_UC) })}
        </p>

        {/* A worked month — exact at both ends, because the menu is closed. */}
        <div className="mx-auto mt-10 max-w-3xl">
          <p className="text-center text-sm font-medium uppercase tracking-wide text-[color:var(--bd-muted)] rtl:normal-case rtl:tracking-normal">
            {t('pricing.uc.examples.label')}
          </p>
          <p
            data-uc-example
            className="mt-4 rounded-lg border border-[color:var(--bd-line)] bg-[color:var(--bd-page)] p-4 text-pretty text-sm text-[color:var(--bd-muted)] sm:text-base"
          >
            {/* Every figure is DERIVED from `usage-example.ts`, never authored
                into the six catalogs — the same single-sourcing the docs worked
                example uses, for the same reason: a number that lives in six
                catalogs has six chances to be wrong and no single place to
                check. */}
            {t('pricing.uc.example', {
              interactions: fmt(EXAMPLE_INTERACTIONS_PER_MONTH),
              simple: fmt(EXAMPLE_SIMPLE_USAGE_CREDITS_PER_MONTH),
              full: fmt(EXAMPLE_FULL_USAGE_CREDITS_PER_MONTH),
              tier: EXAMPLE_TIER.displayName,
              allowance: fmt(EXAMPLE_TIER.includedUsageCredits),
            })}
          </p>
        </div>

        {/* The no-surprise-bills promise. */}
        <ul
          aria-label={t('pricing.uc.promise.label')}
          className="mx-auto mt-10 grid max-w-4xl grid-cols-1 gap-4 sm:grid-cols-3"
        >
          {PROMISE.map(({ id, Icon, key }) => (
            <li
              key={id}
              data-uc-promise={id}
              className="flex items-start gap-3 rounded-lg border border-[color:var(--bd-line)] bg-[color:var(--bd-page)] p-4 text-sm text-[color:var(--bd-muted)]"
            >
              <Icon aria-hidden="true" className="h-5 w-5 shrink-0 text-[color:var(--bd-ink)]" />
              <span>{t(key)}</span>
            </li>
          ))}
        </ul>

        {/* BYOK is one line here, not a duplicative tier×route grid. */}
        <p
          data-uc-byok
          className="mx-auto mt-8 max-w-3xl text-pretty text-center text-sm text-[color:var(--bd-muted)]"
        >
          {t('pricing.uc.byok')}
        </p>

        <div className="mt-8 flex justify-center">
          <a href="#uc-calculator" className={ctaClasses({ variant: 'secondary', size: 'sm' })}>
            {t('pricing.uc.cta.estimate')}
            <CtaArrow />
          </a>
        </div>
      </div>
    </section>
  );
}
